/**
 * BI dashboards (`analytics.*`), merged into the dictionary through `extra.ts`.
 * `analyticsEn` is typed against `analyticsPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const analyticsPl = {
  nav: { mine: 'Mój wynik' },
  print: 'Drukuj / PDF',
  printedAt: 'wygenerowano {date}',
  noData: 'Brak danych dla wybranych filtrów',
  loadError: 'Nie udało się pobrać listy.',
  period: {
    label: 'Okres',
    mtd: 'Ten miesiąc',
    prevMonth: 'Poprzedni miesiąc',
    quarter: 'Kwartał',
    ytd: 'Od początku roku',
    l12: 'Ostatnie 12 mies.',
    custom: 'Własny zakres',
    from: 'Od',
    to: 'Do',
  },
  filters: {
    title: 'Filtry',
    owner: 'Handlowiec',
    rep: 'Wybierz handlowca',
    allOwners: 'Wszyscy handlowcy',
    segment: 'Segment',
    allSegments: 'Wszystkie segmenty',
    region: 'Region',
    allRegions: 'Wszystkie regiony',
    group: 'Grupa produktów',
    allGroups: 'Wszystkie grupy',
    reset: 'Wyczyść',
  },
  basis: {
    label: 'Sprzedaż wg',
    FS: 'faktur',
    WZ: 'wydań (WZ)',
    hint: 'Wg faktur: data faktury, korekty, paragonu - zgodnie z księgami. Wg wydań: data WZ - na bieżąco, także dla klientów z fakturami zbiorczymi (tydzień, dwa, miesiąc).',
  },
  compare: {
    label: 'Porównaj z',
    prev: 'Poprzednim okresem',
    yoy: 'Rokiem poprzednim',
    vsPrev: 'vs poprz. okres',
    vsYoy: 'vs rok temu',
    prevShort: 'Poprz. okres',
    yoyShort: 'Rok temu',
  },
  kpi: {
    sectionSales: 'Wskaźniki sprzedaży',
    sectionCustomers: 'Wskaźniki klientów',
    sales: 'Sprzedaż netto',
    margin: 'Marża',
    marginPct: 'Marża %',
    orders: 'Zamówienia',
    avgOrder: 'Średnie zamówienie',
    active: 'Klienci aktywni',
    new: 'Nowi klienci',
    lost: 'Utraceni klienci',
    recovered: 'Odzyskani klienci',
    visits: 'Wizyty',
    receipts: 'w tym paragony',
    receiptsHint: 'Paragony stałych klientów są wliczone w sprzedaż netto; liczba paragonów: {count}.',
  },
  drill: {
    show: 'Pokaż klientów',
    showAll: 'Pokaż wszystkich',
    empty: 'Brak klientów w tym zestawieniu.',
    customer: 'Klient',
    status: 'Status',
    sales: 'Sprzedaż',
    previous: 'Poprzednio',
    margin: 'Marża',
    lastOrder: 'Ostatnie zam.',
  },
  trend: {
    title: 'Trend 13 miesięcy',
    subtitle: 'Sprzedaż netto, rok do roku i marża %',
    subtitleClick: 'Sprzedaż netto i marża % · kliknij miesiąc, aby zawęzić okres',
    sales: 'Sprzedaż',
    yoy: 'Sprzedaż rok wcześniej',
    marginPct: 'Marża %',
    inProgress: 'w toku',
  },
  target: {
    title: 'Realizacja celu',
    titleTeam: 'Realizacja celu zespołu',
    titleMine: 'Mój cel',
    subtitle: 'Sprzedaż vs target w wybranym okresie',
    realization: 'Realizacja',
    sales: 'Sprzedaż',
    target: 'Cel',
    remaining: 'Do celu',
    surplus: 'Ponad cel',
    pace: 'Oczekiwane tempo: {value} okresu',
    none: 'Brak celu',
    noneHint: 'Dla wybranych filtrów nie zdefiniowano targetu.',
    status: { good: 'Cel osiągnięty', warning: 'Blisko celu', bad: 'Poniżej celu' },
  },
  ranking: {
    title: 'Ranking handlowców',
    subtitle: 'Sprzedaż wg handlowca ze znacznikiem celu',
    subtitleClick: 'Sprzedaż wg handlowca ze znacznikiem celu · kliknij, aby zobaczyć klientów',
    target: 'Cel: {value}',
    margin: 'Marża: {value}',
  },
  rep: {
    title: 'Wyniki handlowców',
    subtitle: 'Sprzedaż, realizacja celu i ruch w portfelu klientów',
    name: 'Handlowiec',
    sales: 'Sprzedaż',
    marginPct: 'Marża %',
    target: 'Cel',
    realization: 'Realizacja',
    delta: 'Zmiana',
    active: 'Aktywni',
    new: 'Nowi',
    lost: 'Utraceni',
    declining: 'Spadkowi',
    visits: 'Wizyty',
    total: 'Razem',
    drill: {
      sales: 'sprzedaż',
      activeCustomers: 'aktywni klienci',
      newCustomers: 'nowi klienci',
      lostCustomers: 'utraceni klienci',
      recoveredCustomers: 'odzyskani klienci',
      declining: 'klienci ze spadkiem',
    },
  },
  flow: {
    title: 'Ruch klientów',
    subtitle: 'Nowi i odzyskani vs utraceni w okresie',
    net: 'bilans klientów',
    active: 'Aktywni',
    newCustomers: 'Nowi',
    recovered: 'Odzyskani',
    lost: 'Utraceni',
  },
  mix: {
    groups: 'Struktura wg grup produktów',
    groupsSub: 'Udział w sprzedaży netto',
    regions: 'Sprzedaż wg regionów',
    regionsSub: 'Netto, liczba klientów i marża',
    segments: 'Sprzedaż wg segmentów',
    segmentsSub: 'Netto, liczba klientów i marża',
    other: 'Pozostałe',
    salesTotal: 'sprzedaż',
    marginShort: 'marża {value}',
    customers: '{count} klientów',
    share: 'Udział: {value}',
  },
  alerts: {
    title: 'Otwarte alerty',
    critical: 'Alerty krytyczne',
    warning: 'Ostrzeżenia',
    opportunity: 'Okazje sprzedażowe',
    byType: 'Alerty wg typu',
    byTypeSub: 'Otwarte alerty zespołu — kliknij, aby przejść do centrum alertów',
    none: 'Brak otwartych alertów',
    types: {
      SALES_DROP: 'Spadek sprzedaży',
      NO_ORDER: 'Brak zamówienia',
      MARGIN_DROP: 'Spadek marży',
      PRODUCT_LOST: 'Utracony produkt',
      CROSS_SELL: 'Cross-sell',
      VISIT_DUE: 'Wizyta do wykonania',
      DELIVERY_FAILED: 'Nieudana dostawa',
      DELIVERY_LATE: 'Dostawa po terminie',
    },
  },
  team: {
    subtitle: 'Wyniki zespołu, realizacja celów, lejek leadów i klienci wymagający reakcji.',
    comparison: 'Porównanie handlowców',
    comparisonSub: 'Zmiana sprzedaży vs poprzedni okres',
    deltaLabel: 'Zmiana',
    growth: 'Wzrost',
    decline: 'Spadek',
  },
  funnel: {
    title: 'Lejek leadów',
    subtitle: 'Liczba leadów i potencjał na etapach',
    none: 'Brak leadów w lejku',
    conversion: '{value} z poprz.',
    stages: {
      NEW: 'Nowy',
      CONTACTED: 'Skontaktowany',
      QUALIFIED: 'Zakwalifikowany',
      VISIT: 'Wizyta',
      OFFER: 'Oferta',
      APPROVAL: 'Akceptacja',
      WON: 'Wygrany',
      LOST: 'Przegrany',
    },
  },
  declining: {
    title: 'Klienci ze spadkiem',
    subtitle: 'Sprzedaż niższa o ≥ 15% vs poprzedni okres',
    none: 'Brak klientów ze spadkiem — dobra robota.',
  },
  top: {
    title: 'Najwięksi klienci',
    subtitle: 'Sprzedaż w okresie i zmiana vs poprzedni okres',
  },
  opportunities: {
    title: 'Okazje',
    subtitle: 'Klienci z lukami w grupach produktów',
    customers: 'klientów z luką',
    potential: 'szac. potencjał / mies.',
    none: 'Brak zidentyfikowanych okazji.',
    hint: 'Luka = grupa produktów, z której klient kupuje tylko część asortymentu.',
  },
  board: { subtitle: 'Sprzedaż, marża, cele i ruch w bazie klientów — widok dla zarządu.' },
  me: {
    subtitle: 'Twoja sprzedaż vs cel, najwięksi klienci i okazje.',
    subtitleManager: 'Wynik wybranego handlowca: sprzedaż vs cel, klienci i okazje.',
    resultOf: 'Wynik:',
  },
};

export const analyticsEn: Shape<typeof analyticsPl> = {
  nav: { mine: 'My results' },
  print: 'Print / PDF',
  printedAt: 'generated {date}',
  noData: 'No data for the selected filters',
  loadError: 'Could not load the list.',
  period: {
    label: 'Period',
    mtd: 'This month',
    prevMonth: 'Previous month',
    quarter: 'Quarter',
    ytd: 'Year to date',
    l12: 'Last 12 months',
    custom: 'Custom range',
    from: 'From',
    to: 'To',
  },
  filters: {
    title: 'Filters',
    owner: 'Sales rep',
    rep: 'Choose a sales rep',
    allOwners: 'All sales reps',
    segment: 'Segment',
    allSegments: 'All segments',
    region: 'Region',
    allRegions: 'All regions',
    group: 'Product group',
    allGroups: 'All groups',
    reset: 'Clear',
  },
  basis: {
    label: 'Sales by',
    FS: 'invoice',
    WZ: 'delivery (WZ)',
    hint: 'By invoice: invoice, correction, receipt date - as booked. By delivery: WZ date - current, also for customers with collective invoices (weekly, bi-weekly, monthly).',
  },
  compare: {
    label: 'Compare with',
    prev: 'Previous period',
    yoy: 'Previous year',
    vsPrev: 'vs prev. period',
    vsYoy: 'vs last year',
    prevShort: 'Prev. period',
    yoyShort: 'Last year',
  },
  kpi: {
    sectionSales: 'Sales indicators',
    sectionCustomers: 'Customer indicators',
    sales: 'Net sales',
    margin: 'Margin',
    marginPct: 'Margin %',
    orders: 'Orders',
    avgOrder: 'Average order',
    active: 'Active customers',
    new: 'New customers',
    lost: 'Lost customers',
    recovered: 'Recovered customers',
    visits: 'Visits',
    receipts: 'incl. receipts',
    receiptsHint: 'Receipts of regular customers are included in net sales; number of receipts: {count}.',
  },
  drill: {
    show: 'Show customers',
    showAll: 'Show all',
    empty: 'No customers in this breakdown.',
    customer: 'Customer',
    status: 'Status',
    sales: 'Sales',
    previous: 'Previous',
    margin: 'Margin',
    lastOrder: 'Last order',
  },
  trend: {
    title: '13-month trend',
    subtitle: 'Net sales, year over year and margin %',
    subtitleClick: 'Net sales and margin % · click a month to narrow the period',
    sales: 'Sales',
    yoy: 'Sales a year earlier',
    marginPct: 'Margin %',
    inProgress: 'in progress',
  },
  target: {
    title: 'Target attainment',
    titleTeam: 'Team target attainment',
    titleMine: 'My target',
    subtitle: 'Sales vs target in the selected period',
    realization: 'Attainment',
    sales: 'Sales',
    target: 'Target',
    remaining: 'To target',
    surplus: 'Above target',
    pace: 'Expected pace: {value} of the period',
    none: 'No target',
    noneHint: 'No target is defined for the selected filters.',
    status: { good: 'Target reached', warning: 'Close to target', bad: 'Below target' },
  },
  ranking: {
    title: 'Sales rep ranking',
    subtitle: 'Sales per rep with a target marker',
    subtitleClick: 'Sales per rep with a target marker · click to see customers',
    target: 'Target: {value}',
    margin: 'Margin: {value}',
  },
  rep: {
    title: 'Sales rep performance',
    subtitle: 'Sales, target attainment and customer portfolio movement',
    name: 'Sales rep',
    sales: 'Sales',
    marginPct: 'Margin %',
    target: 'Target',
    realization: 'Attainment',
    delta: 'Change',
    active: 'Active',
    new: 'New',
    lost: 'Lost',
    declining: 'Declining',
    visits: 'Visits',
    total: 'Total',
    drill: {
      sales: 'sales',
      activeCustomers: 'active customers',
      newCustomers: 'new customers',
      lostCustomers: 'lost customers',
      recoveredCustomers: 'recovered customers',
      declining: 'declining customers',
    },
  },
  flow: {
    title: 'Customer movement',
    subtitle: 'New and recovered vs lost in the period',
    net: 'net customer change',
    active: 'Active',
    newCustomers: 'New',
    recovered: 'Recovered',
    lost: 'Lost',
  },
  mix: {
    groups: 'Mix by product group',
    groupsSub: 'Share of net sales',
    regions: 'Sales by region',
    regionsSub: 'Net, customers and margin',
    segments: 'Sales by segment',
    segmentsSub: 'Net, customers and margin',
    other: 'Other',
    salesTotal: 'sales',
    marginShort: 'margin {value}',
    customers: '{count} customers',
    share: 'Share: {value}',
  },
  alerts: {
    title: 'Open alerts',
    critical: 'Critical alerts',
    warning: 'Warnings',
    opportunity: 'Sales opportunities',
    byType: 'Alerts by type',
    byTypeSub: 'Open team alerts — click to open the alert centre',
    none: 'No open alerts',
    types: {
      SALES_DROP: 'Sales drop',
      NO_ORDER: 'No order',
      MARGIN_DROP: 'Margin drop',
      PRODUCT_LOST: 'Product lost',
      CROSS_SELL: 'Cross-sell',
      VISIT_DUE: 'Visit due',
      DELIVERY_FAILED: 'Failed delivery',
      DELIVERY_LATE: 'Late delivery',
    },
  },
  team: {
    subtitle: 'Team results, target attainment, lead funnel and customers that need action.',
    comparison: 'Sales rep comparison',
    comparisonSub: 'Sales change vs previous period',
    deltaLabel: 'Change',
    growth: 'Growth',
    decline: 'Decline',
  },
  funnel: {
    title: 'Lead funnel',
    subtitle: 'Number of leads and potential per stage',
    none: 'No leads in the funnel',
    conversion: '{value} of prev.',
    stages: {
      NEW: 'New',
      CONTACTED: 'Contacted',
      QUALIFIED: 'Qualified',
      VISIT: 'Visit',
      OFFER: 'Offer',
      APPROVAL: 'Approval',
      WON: 'Won',
      LOST: 'Lost',
    },
  },
  declining: {
    title: 'Declining customers',
    subtitle: 'Sales down ≥ 15% vs previous period',
    none: 'No declining customers — well done.',
  },
  top: {
    title: 'Top customers',
    subtitle: 'Sales in the period and change vs previous period',
  },
  opportunities: {
    title: 'Opportunities',
    subtitle: 'Customers with product-group gaps',
    customers: 'customers with a gap',
    potential: 'est. potential / month',
    none: 'No opportunities identified.',
    hint: 'Gap = a product group where the customer buys only part of the range.',
  },
  board: { subtitle: 'Sales, margin, targets and customer base movement — the board view.' },
  me: {
    subtitle: 'Your sales vs target, top customers and opportunities.',
    subtitleManager: 'Selected sales rep: sales vs target, customers and opportunities.',
    resultOf: 'Results of',
  },
};
